import { useCallback, useEffect, useRef, useState } from "react";
import {
  HAND_REANNOUNCE_MS,
  ackFrame,
  authJoinFrame,
  handFrame,
  joinFrame,
  parseHand,
  parseHandAck,
  parseJoined,
  readWsGrant,
  relayAvailable,
  relayUrl,
  typedHandFrame,
  type HandSignal,
  type RelayRole,
  type WsGrant,
} from "rc-app/utils/guidedRoomRelay";
import { getRoomWsToken } from "rc-app/api/guidedRoomActionsApi";

/* ── The room's side channel, connected ────────────────────────────────
   Owns one socket to the sync relay for the life of a room, and turns it
   into two things the pages actually want: "tell everyone my hand is up"
   and "someone's hand went up". The frame shapes, and why they look the
   way they do, are in utils/guidedRoomRelay.ts.

   Kept separate from useGuidedRoomVoice deliberately. Voice can fail on
   its own terms — a denied microphone, an expired Agora token — and none
   of that should cost a buyer the ability to raise their hand. They are
   two transports and they fail independently.

   ── FAILURE POSTURE ──
   This channel is best-effort. There is nothing to tell a buyer if the
   relay is down: a hand is a nudge, not a transaction, and an error
   banner about a websocket during a live property tour is noise about
   something they can solve by speaking. So a dead socket retries quietly
   and the button keeps working locally. `connected` is exposed for the
   host, who is the one who benefits from knowing the queue may be stale. */

const RETRY_MIN_MS = 2000;
const RETRY_MAX_MS = 20000;

export type GuidedRoomRelay = {
  /** The socket is open and joined. Local state is unaffected by this. */
  connected: boolean;
  /** False when no relay is configured for this environment — the
      caller should disable the control rather than pretend. */
  available: boolean;
  /** True once the AUTHENTICATED relay accepted our join token. Legacy
      relay never sets it. Observed, not configured. */
  authed: boolean;
  /** Announce our hand. Safe to call while disconnected — see below. */
  sendHand: (raised: boolean) => void;
  /** Host only: answer someone, which puts their hand down. */
  sendAck: (targetSessionId: string) => void;
};

type Credential = { session_id: string; session_secret: string } | null | undefined;

type Options = {
  enabled?: boolean;
  /** Buys the ws_token. Without it we connect unauthenticated, which only
      the legacy relay accepts. */
  credential?: Credential;
  /** Shown by a host whose roster hasn't caught up with a new arrival. */
  displayName?: string;
  onHand?: (sig: HandSignal) => void;
  /** Guest only: the host answered us, so our own hand goes down. */
  onAck?: () => void;
};

export const useGuidedRoomRelay = (
  roomId: string,
  selfId: string,
  role: RelayRole,
  { enabled = true, displayName = "", credential, onHand, onAck }: Options = {},
): GuidedRoomRelay => {
  const [connected, setConnected] = useState(false);

  /* Primitives, because a page that rebuilds its credential object each
     render would otherwise tear down and rebuild this socket every time
     anything else on the page changed. */
  const credId = credential?.session_id || "";
  const credSecret = credential?.session_secret || "";

  const sockRef = useRef<WebSocket | null>(null);
  const deadRef = useRef(false);
  const retryRef = useRef<any>(null);
  const beatRef = useRef<any>(null);

  /* Our own hand, as last set. Held in a ref rather than state because
     two things read it that must not re-run when it changes: the
     re-announce timer, and the reconnect handler that re-states it on a
     fresh socket. Making it state would tear down the socket on every
     raise. */
  const raisedRef = useRef(false);

  /* Latest callback and name without either becoming a reason to
     reconnect. A page that rebuilds `onHand` each render — which is
     every page that doesn't wrap it in useCallback — would otherwise
     drop and rebuild the socket on every keystroke elsewhere. */
  const onHandRef = useRef(onHand);
  const onAckRef = useRef(onAck);
  const nameRef = useRef(displayName);
  useEffect(() => { onHandRef.current = onHand; }, [onHand]);
  useEffect(() => { onAckRef.current = onAck; }, [onAck]);
  useEffect(() => { nameRef.current = displayName; }, [displayName]);

  const send = useCallback((frame: string) => {
    const s = sockRef.current;
    if (!s || s.readyState !== WebSocket.OPEN) return false;
    try { s.send(frame); return true; } catch { return false; }
  }, []);

  /* Which dialect this socket speaks. Set by the {type:"joined"} frame the
     authenticated relay sends and nothing else does — so it is observed,
     never configured. A ref because the send path reads it and must not
     re-render or re-subscribe when it flips. */
  const authedRef = useRef(false);
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    if (!enabled || !roomId || !selfId || !relayAvailable()) return undefined;

    deadRef.current = false;
    let attempt = 0;
    let grant: WsGrant | null = null;

    /* Buy a token if we can. Failure is NOT fatal: the endpoint doesn't
       exist on every environment yet, and the legacy relay wants no token
       at all. We try, we shrug, we connect with what we have — and the
       authenticated relay will close us 4401 if that wasn't good enough,
       which is the correct outcome rather than something to pre-empt. */
    const fetchGrant = async (): Promise<void> => {
      if (!credId || !credSecret) return;
      try {
        const body = await getRoomWsToken(roomId, { session_id: credId, session_secret: credSecret });
        grant = readWsGrant(body, roomId);
      } catch {
        grant = null;              // 404 while undeployed, 403 if not admitted
      }
    };

    const clearTimers = () => {
      if (retryRef.current) { clearTimeout(retryRef.current); retryRef.current = null; }
      if (beatRef.current) { clearInterval(beatRef.current); beatRef.current = null; }
    };

    const scheduleRetry = () => {
      if (deadRef.current || retryRef.current) return;
      /* Exponential up to a cap. A room can sit open for an hour; a
         relay that's down stays down, and hammering it every two seconds
         for an hour helps nobody. */
      const wait = Math.min(RETRY_MAX_MS, RETRY_MIN_MS * 2 ** Math.min(attempt, 4));
      attempt += 1;
      retryRef.current = setTimeout(() => { retryRef.current = null; connect(); }, wait);
    };

    /* The hand, in whichever dialect this socket turned out to speak. */
    const handOut = (raised: boolean) =>
      (authedRef.current ? typedHandFrame : handFrame)(roomId, selfId, raised, nameRef.current);

    function connect() {
      if (deadRef.current) return;
      let sock: WebSocket;
      try {
        /* The backend's URL wins when it gave us one — a token signed for
           one relay is worthless at another. */
        sock = new WebSocket(grant?.url || relayUrl());
      } catch {
        scheduleRetry();
        return;
      }
      sockRef.current = sock;

      sock.addEventListener("open", () => {
        if (deadRef.current) { try { sock.close(); } catch { /* closing anyway */ } return; }
        attempt = 0;
        setConnected(true);
        /* Assume legacy until told otherwise. The authenticated relay
           replies {type:"joined"} and we switch dialect there; if we had
           no token it will instead close us 4401, which the drop handler
           surfaces rather than retrying blindly. */
        authedRef.current = false;
        setAuthed(false);
        try {
          sock.send(grant
            ? authJoinFrame(grant.roomId || roomId, selfId, role, grant.token)
            : joinFrame(roomId, selfId, role));
        } catch { /* the close handler will retry */ }

        /* Re-state a hand that was already up. A reconnect is exactly the
           case where the host has heard nothing from us for a while, so
           saying nothing here is how a hand silently disappears. */
        if (raisedRef.current) {
          try { sock.send(handOut(true)); } catch { /* retried by the beat */ }
        }

        if (beatRef.current) clearInterval(beatRef.current);
        beatRef.current = setInterval(() => {
          if (!raisedRef.current) return;          // nothing to re-state
          try { sock.send(handOut(true)); } catch { /* next beat */ }
        }, HAND_REANNOUNCE_MS);
      });

      sock.addEventListener("message", (e: MessageEvent) => {
        if (deadRef.current) return;

        /* The authenticated relay accepted us. Switch dialect, and
           re-announce a raised hand immediately — the frame we sent on
           open went out in the wrong shape and that server dropped it. */
        const joined = parseJoined(e?.data, grant?.roomId || roomId);
        if (joined) {
          authedRef.current = true;
          setAuthed(true);
          if (raisedRef.current) {
            try { sock.send(handOut(true)); } catch { /* the beat retries */ }
          }
          return;
        }

        /* An ack aimed at us lowers our own hand, and lowers the ref too
           — otherwise the re-announce timer would put it straight back up
           a few seconds later and the host would watch a hand they just
           answered rise from the dead. */
        if (parseHandAck(e?.data, roomId, selfId)) {
          raisedRef.current = false;
          onAckRef.current?.();
          return;
        }
        const sig = parseHand(e?.data, roomId, selfId);
        if (sig) onHandRef.current?.(sig);
      });

      /* One handler for both: a socket that errors also closes, and the
         only useful response to either is to try again.

         Except when the relay refused us on purpose. 4401/4403/4409 are
         auth verdicts, not glitches — the same token will be refused
         every time, so retrying is a reconnect loop that hammers the
         server and hides the real problem. Fetch a fresh grant and let
         the backoff space the attempts out. */
      const drop = (ev: any) => {
        if (deadRef.current) return;
        setConnected(false);
        authedRef.current = false;
        setAuthed(false);
        if (beatRef.current) { clearInterval(beatRef.current); beatRef.current = null; }
        const code = Number(ev?.code || 0);
        if (code === 4401 || code === 4403 || code === 4409) {
          grant = null;
          void fetchGrant().then(() => { if (!deadRef.current) scheduleRetry(); });
          return;
        }
        scheduleRetry();
      };
      sock.addEventListener("error", drop);
      sock.addEventListener("close", drop);
    }

    /* Token first, then connect. The await is why this is an async IIFE
       rather than a bare call: connecting before the grant lands would
       join unauthenticated and get closed immediately. */
    void fetchGrant().then(() => { if (!deadRef.current) connect(); });

    return () => {
      deadRef.current = true;
      clearTimers();
      const s = sockRef.current;
      sockRef.current = null;
      /* Drop the hand on the way out so a buyer who closes the tab
         doesn't leave a request the host can never clear. Best-effort:
         if the socket is already gone, the host's roster prune catches
         it when they disappear from the people list. */
      if (s && s.readyState === WebSocket.OPEN && raisedRef.current) {
        try { s.send(handFrame(roomId, selfId, false, nameRef.current)); } catch { /* leaving anyway */ }
      }
      try { s?.close(); } catch { /* already closed */ }
      setConnected(false);
    };
  }, [enabled, roomId, selfId, role, credId, credSecret]);

  /* Local state updates whether or not the frame goes out. The buyer
     pressed a button; the button must look pressed. If the socket is
     down, the re-announce on reconnect is what eventually tells the
     room — which is why raisedRef is set before the send is attempted. */
  const sendHand = useCallback((raised: boolean) => {
    raisedRef.current = !!raised;
    const build = authedRef.current ? typedHandFrame : handFrame;
    send(build(roomId, selfId, !!raised, nameRef.current));
  }, [roomId, selfId, send]);

  /* Unlike sendHand there's nothing local to keep in step here — the
     host's own queue is cleared by the caller, because a host who clicks
     an answered question expects the row to go whether or not a socket
     was open. */
  const sendAck = useCallback((targetSessionId: string) => {
    if (!targetSessionId) return;
    send(ackFrame(roomId, selfId, targetSessionId));
  }, [roomId, selfId, send]);

  return { connected, available: relayAvailable(), authed, sendHand, sendAck };
};

export default useGuidedRoomRelay;
